import { useHistory, useRouteMatch } from "react-router-dom";
import { StepConfiguration, StepStatusState } from "./stepsInterfaces";
import {
  activateStartStep,
  clearStepStatus,
  registerSteps,
} from "./stepsSlice";

import { isDefined } from "Utils/functions";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useStepStatusSelector } from "./stepsSelectors";
import { findStep } from "./stepsUtils";

export const useSteps = (
  stepConfiguration: StepConfiguration,
): StepStatusState => {
  useStepsCleanup();
  return useRegisterSteps(stepConfiguration);
};

const useRegisterSteps = (
  stepConfiguration: StepConfiguration,
): StepStatusState => {
  const dispatch = useDispatch();
  const stepStatus = useStepStatusSelector();

  useEffect(() => {
    if (stepConfiguration.steps.length > 0 && stepStatus.steps.length === 0) {
      dispatch(registerSteps(stepConfiguration));
    }
  }, [stepConfiguration.steps, stepStatus.steps]);

  return stepStatus;
};

const useStepsCleanup = (): void => {
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      dispatch(clearStepStatus());
    };
  }, []);
};

export const useStepsActiveStepNavigation = (): void => {
  const history = useHistory();
  const { activeStepId, steps } = useStepStatusSelector();
  useEffect(() => {
    const activeStep = findStep(steps, activeStepId);
    if (
      isDefined(activeStep) &&
      isDefined(activeStep.uri) &&
      history.location.pathname !== activeStep.uri
    ) {
      history.replace({
        pathname: activeStep.uri,
        search: history.location.search,
      });
    }
  }, [steps, activeStepId, history.location.pathname]);
};

export const useStepRouteRedirect = (): void => {
  const dispatch = useDispatch();
  const { steps, activeStepId, startStepId } = useStepStatusSelector();
  const stepRoutes = steps.map((step) => step.uri).filter(isDefined);
  const stepRoutesMatch = useRouteMatch(stepRoutes);

  useEffect(() => {
    const hasActiveStep = isDefined(findStep(steps, activeStepId));
    const hasStartStep = isDefined(findStep(steps, startStepId));
    const isOnStepRoute = isDefined(stepRoutesMatch) && stepRoutesMatch.isExact;

    if (!hasActiveStep && hasStartStep && isOnStepRoute) {
      dispatch(activateStartStep());
    }
  }, [steps, activeStepId, startStepId, stepRoutesMatch?.isExact]);
};
